import { invisiblyApi } from 'src/redux/api/invisibly-api';
import { APITagType } from 'src/redux/api/invisibly-api.types';
import type {
  AdminSubscription,
  AdminSubscriptionListResponse,
  ListAdminSubscriptionsParams,
  ListPortalUsersParams,
  PortalMetrics,
  PortalUsageLimitConfig,
  PortalUser,
  PortalUserSubscriptionTier,
  PortalUsersPaginatedResponse,
} from '../types/users.types';

/** Portal user-management proxies to self-service; allow headroom above backend upstream timeout. */
const PORTAL_MANAGE_PROXY_TIMEOUT_MS = 90_000;

const portalUsersEndpoints = invisiblyApi.injectEndpoints({
  endpoints: (builder) => ({
    getPortalUsers: builder.query<PortalUsersPaginatedResponse, ListPortalUsersParams | void>({
      query: (params) => ({
        method: 'GET',
        url: 'b2b/manage/users',
        params: params ?? {},
        handleErrorGlobally: false,
        timeout: PORTAL_MANAGE_PROXY_TIMEOUT_MS,
      }),
      providesTags: [{ id: 'LIST', type: APITagType.PortalUsers }],
    }),
    getPortalUser: builder.query<PortalUser, string>({
      query: (userId) => ({
        method: 'GET',
        url: `b2b/manage/users/${userId}`,
        handleErrorGlobally: false,
      }),
      providesTags: (_result, _error, userId) => [{ id: userId, type: APITagType.PortalUsers }],
    }),
    blockPortalUser: builder.mutation<PortalUser, string>({
      query: (userId) => ({
        method: 'POST',
        url: `b2b/manage/users/${userId}/block`,
        data: {},
        handleErrorGlobally: false,
      }),
      invalidatesTags: [{ id: 'LIST', type: APITagType.PortalUsers }],
    }),
    unblockPortalUser: builder.mutation<PortalUser, string>({
      query: (userId) => ({
        method: 'POST',
        url: `b2b/manage/users/${userId}/unblock`,
        data: {},
        handleErrorGlobally: false,
      }),
      invalidatesTags: [{ id: 'LIST', type: APITagType.PortalUsers }],
    }),
    setPortalUserQuotaOverride: builder.mutation<
    PortalUser,
    { userId: string; creative_quota_override: number | null }
    >({
      query: ({ userId, creative_quota_override }) => ({
        method: 'PATCH',
        url: `b2b/manage/users/${userId}/quota-override`,
        data: { creative_quota_override },
        handleErrorGlobally: false,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { id: 'LIST', type: APITagType.PortalUsers },
        { id: userId, type: APITagType.PortalUsers },
      ],
    }),
    getPortalUsageLimitConfig: builder.query<PortalUsageLimitConfig, void>({
      query: () => ({
        method: 'GET',
        url: 'b2b/manage/usage-limit-config',
        handleErrorGlobally: false,
      }),
      providesTags: [{ id: 'CONFIG', type: APITagType.PortalUsageLimitConfig }],
    }),
    updatePortalUsageLimitConfig: builder.mutation<PortalUsageLimitConfig, number>({
      query: (monthlyCreativeLimit) => ({
        method: 'PATCH',
        url: 'b2b/manage/usage-limit-config',
        data: { monthly_creative_limit: monthlyCreativeLimit },
        handleErrorGlobally: false,
      }),
      invalidatesTags: [{ id: 'CONFIG', type: APITagType.PortalUsageLimitConfig }],
    }),
    getPortalMetrics: builder.query<PortalMetrics, void>({
      query: () => ({
        method: 'GET',
        url: 'b2b/manage/metrics',
        handleErrorGlobally: false,
        timeout: PORTAL_MANAGE_PROXY_TIMEOUT_MS,
      }),
      providesTags: [{ id: 'METRICS', type: APITagType.PortalMetrics }],
    }),
    getPortalSubscription: builder.query<AdminSubscription, string>({
      query: (workspaceId) => ({
        method: 'GET',
        url: `b2b/manage/subscriptions/${workspaceId}`,
        handleErrorGlobally: false,
      }),
      providesTags: (_result, _error, workspaceId) => [
        { id: workspaceId, type: APITagType.PortalSubscriptions },
      ],
    }),
    listPortalSubscriptions: builder.query<AdminSubscriptionListResponse, ListAdminSubscriptionsParams | void>({
      query: (params) => ({
        method: 'GET',
        url: 'b2b/manage/subscriptions',
        params: params ?? {},
        handleErrorGlobally: false,
        timeout: PORTAL_MANAGE_PROXY_TIMEOUT_MS,
      }),
      providesTags: [{ id: 'LIST', type: APITagType.PortalSubscriptions }],
    }),
    updatePortalSubscriptionTier: builder.mutation<
    AdminSubscription,
    { workspaceId: string; tier: PortalUserSubscriptionTier }
    >({
      query: ({ workspaceId, tier }) => ({
        method: 'PATCH',
        url: `b2b/manage/subscriptions/${workspaceId}`,
        data: { tier },
        handleErrorGlobally: false,
      }),
      invalidatesTags: (_result, _error, { workspaceId }) => [
        { id: workspaceId, type: APITagType.PortalSubscriptions },
        { id: 'LIST', type: APITagType.PortalUsers },
        { id: 'LIST', type: APITagType.PortalSubscriptions },
      ],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetPortalUsersQuery,
  useGetPortalUserQuery,
  useBlockPortalUserMutation,
  useUnblockPortalUserMutation,
  useSetPortalUserQuotaOverrideMutation,
  useGetPortalUsageLimitConfigQuery,
  useUpdatePortalUsageLimitConfigMutation,
  useGetPortalMetricsQuery,
  useGetPortalSubscriptionQuery,
  useListPortalSubscriptionsQuery,
  useUpdatePortalSubscriptionTierMutation,
} = portalUsersEndpoints;

export function isPortalSyncNotConfiguredError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const responseData = (error as { responseData?: Record<string, unknown> }).responseData;
  return responseData?.error_code === 'portal_sync_not_configured';
}
